import React, { useMemo, useState } from 'react';
import { ArrowDownTrayIcon, ArrowUpTrayIcon, CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button, Modal, Spinner } from '../ui';
import { ExcelImportColumn, ExcelImportRow, exportExcelTemplate, parseExcelFile } from '../../utils/excelImport';

const PREVIEW_ROW_LIMIT = 50;

interface ExcelImportModalProps {
  isOpen: boolean;
  title: string;
  schema: ExcelImportColumn[];
  rowsLabel: string;
  templateFileName: string;
  onClose: () => void;
  onConfirm: (rows: ExcelImportRow[]) => Promise<void> | void;
  progressText?: string;
}

const ExcelImportModal: React.FC<ExcelImportModalProps> = ({
  isOpen,
  title,
  schema,
  rowsLabel,
  templateFileName,
  onClose,
  onConfirm,
  progressText,
}) => {
  const [rows, setRows] = useState<ExcelImportRow[]>([]);
  const [fileName, setFileName] = useState('');
  const [loadingFile, setLoadingFile] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const filledCount = useMemo(() => rows.filter((row) => row.__status === 'filled').length, [rows]);
  const visibleRows = useMemo(() => rows.slice(0, PREVIEW_ROW_LIMIT), [rows]);

  const reset = () => {
    setRows([]);
    setFileName('');
    setError('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setLoadingFile(true);
      setError('');
      const parsedRows = await parseExcelFile(file, schema);
      setRows(parsedRows);
      setFileName(file.name);
      if (parsedRows.length === 0) {
        setError('The first sheet has no data rows.');
      }
    } catch (err) {
      console.error('Failed to parse Excel file', err);
      setError('Failed to read the Excel file.');
    } finally {
      setLoadingFile(false);
      event.target.value = '';
    }
  };

  const handleConfirm = async () => {
    if (rows.length === 0) return;

    try {
      setSubmitting(true);
      setError('');
      await new Promise(resolve => setTimeout(resolve, 0));
      await onConfirm(rows);
      reset();
      onClose();
    } catch (err: any) {
      console.error('Failed to import rows', err);
      setError(err.response?.data?.message || err.message || 'Failed to import rows.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={title} size="xl">
      <div className="space-y-5">
        {error && (
          <div className="rounded-md border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700">
            {error}
          </div>
        )}

        {progressText && (
          <div className="rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-700">
            {progressText}
          </div>
        )}

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            <label className="inline-flex cursor-pointer items-center rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-700 shadow-sm hover:bg-neutral-50">
              <ArrowUpTrayIcon className="mr-2 h-4 w-4" />
              Choose Excel
              <input type="file" accept=".xlsx,.csv" className="sr-only" onChange={handleFileChange} />
            </label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => exportExcelTemplate(schema, templateFileName)}
              leftIcon={<ArrowDownTrayIcon className="h-4 w-4" />}
            >
              Template
            </Button>
          </div>
          <div className="text-sm text-neutral-500">
            {fileName ? `${fileName} - ${rows.length} ${rowsLabel}` : `0 ${rowsLabel}`}
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-neutral-200">
          <div className="max-h-44 overflow-auto">
            <table className="min-w-full divide-y divide-neutral-200 text-sm">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="whitespace-nowrap px-3 py-2 text-left font-semibold text-neutral-700">Column</th>
                  <th className="whitespace-nowrap px-3 py-2 text-left font-semibold text-neutral-700">Required</th>
                  <th className="whitespace-nowrap px-3 py-2 text-left font-semibold text-neutral-700">Example</th>
                  <th className="whitespace-nowrap px-3 py-2 text-left font-semibold text-neutral-700">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 bg-white">
                {schema.map((column) => (
                  <tr key={column.key}>
                    <td className="whitespace-nowrap px-3 py-2 font-medium text-neutral-900">{column.label}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-neutral-600">{column.required ? 'Yes' : 'No'}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-neutral-600">{column.example || '-'}</td>
                    <td className="px-3 py-2 text-neutral-500">{column.description || (column.required ? 'Filled with a safe placeholder when blank.' : 'Blank cells import as null.')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-medium text-neutral-800">Preview</p>
            {rows.length > 0 && (
              <p className="text-xs text-neutral-500">
                Showing {Math.min(rows.length, PREVIEW_ROW_LIMIT)} of {rows.length}; {filledCount} row{filledCount === 1 ? '' : 's'} filled missing required values
              </p>
            )}
          </div>

          <div className="overflow-hidden rounded-lg border border-neutral-200">
            <div className="max-h-72 overflow-auto">
              {loadingFile ? (
                <div className="flex h-40 items-center justify-center">
                  <Spinner size="lg" />
                </div>
              ) : rows.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-neutral-500">
                  Select an Excel file to preview rows.
                </div>
              ) : (
                <table className="min-w-full divide-y divide-neutral-200 text-sm">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="whitespace-nowrap px-3 py-2 text-left font-semibold text-neutral-700">Row</th>
                      <th className="whitespace-nowrap px-3 py-2 text-left font-semibold text-neutral-700">Status</th>
                      {schema.map((column) => (
                        <th key={column.key} className="whitespace-nowrap px-3 py-2 text-left font-semibold text-neutral-700">
                          {column.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100 bg-white">
                    {visibleRows.map((row) => (
                      <tr key={row.__rowNumber}>
                        <td className="whitespace-nowrap px-3 py-2 text-neutral-600">{row.__rowNumber}</td>
                        <td className="whitespace-nowrap px-3 py-2">
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${row.__status === 'ready' ? 'bg-success-50 text-success-700' : 'bg-warning-50 text-warning-700'}`}>
                            {row.__status === 'ready' ? 'Ready' : 'Filled'}
                          </span>
                          {row.__notes.length > 0 && (
                            <div className="mt-1 text-xs text-neutral-500">{row.__notes.join(', ')}</div>
                          )}
                        </td>
                        {schema.map((column) => (
                          <td key={column.key} className="whitespace-nowrap px-3 py-2 text-neutral-700">
                            {row[column.key] === null || row[column.key] === undefined ? (
                              <span className="text-neutral-400">null</span>
                            ) : (
                              String(row[column.key])
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={handleClose} leftIcon={<XMarkIcon className="h-4 w-4" />}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleConfirm}
            disabled={rows.length === 0 || submitting}
            isLoading={submitting}
            leftIcon={<CheckCircleIcon className="h-4 w-4" />}
          >
            Confirm Import
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ExcelImportModal;
